// Cold-start cache for a conversation's chosen CAD kernel.
//
// Architecture: `conversation.settings.outputFormat` in Supabase is the
// canonical source of truth (shared across devices, queryable from edge
// functions). This localStorage cache exists for two reasons:
//
//   1. Instant initial render on EditorView mount — the conversation row
//      from Supabase is async, but the cached value lets us paint the
//      correct dropdown immediately without a Solidbody → Mesh flicker
//      when the row eventually loads.
//
//   2. Bridge for the (rare) case where a conversation was created but its
//      settings.outputFormat is missing (older row, partial write failure,
//      race). We can recover the user's choice and self-heal settings.
//
// Settings always wins on disagreement. localStorage is a write-through
// cache: every confirmed settings write also updates this cache.
//
// All filesystem operations are wrapped in try/catch — localStorage can
// be disabled (incognito, storage quota, embedded webviews) and we never
// want a cache miss to be fatal to the app.
//
// The keys are namespaced to avoid colliding with the many other
// localStorage keys this app uses (session, device_id, mesh-topology, etc.)

export type OutputFormat = 'mesh' | 'solidbody' | 'unitcell' | 'implicit';

const KEY_PREFIX = 'telekinesis:outputFormat:';

const isValidFormat = (v: unknown): v is OutputFormat =>
  v === 'mesh' || v === 'solidbody' || v === 'unitcell' || v === 'implicit';

/**
 * Read a conversation's cached kernel preference from localStorage.
 * Returns `undefined` if no cache, if localStorage is disabled, or if the
 * stored value is malformed (e.g. user tampered with dev tools).
 */
export function getCachedOutputFormat(
  conversationId: string,
): OutputFormat | undefined {
  if (!conversationId) return undefined;
  try {
    const raw = localStorage.getItem(KEY_PREFIX + conversationId);
    return isValidFormat(raw) ? raw : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Write a conversation's kernel preference to localStorage.
 * No-op (silently swallows the error) if localStorage isn't available so
 * the rest of the app keeps working — settings-only mode is acceptable.
 */
export function setCachedOutputFormat(
  conversationId: string,
  format: OutputFormat,
): void {
  if (!conversationId) return;
  try {
    localStorage.setItem(KEY_PREFIX + conversationId, format);
  } catch {
    // Storage disabled or full — keep going, the canonical settings write
    // is what actually matters.
  }
}

/**
 * Remove a conversation's cached preference. Useful when a conversation
 * is deleted, or when we detect the cache disagrees with settings and we
 * want to clear stale state before re-writing.
 */
export function clearCachedOutputFormat(conversationId: string): void {
  if (!conversationId) return;
  try {
    localStorage.removeItem(KEY_PREFIX + conversationId);
  } catch {
    // Same as above — swallow.
  }
}
